import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface CustomRole {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  base_role: string | null;
  color: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomRolePermission {
  id: string;
  custom_role_id: string;
  permission_id: string;
  created_at: string;
}

export interface CustomRoleWithPermissions extends CustomRole {
  permissions: string[]; // permission codes
}

export interface CreateCustomRoleInput {
  name: string;
  description?: string;
  base_role?: string;
  color?: string;
  permissions?: string[]; // permission IDs
}

export interface UpdateCustomRoleInput {
  name?: string;
  description?: string;
  base_role?: string | null;
  color?: string;
  is_active?: boolean;
}

export function useCustomRoles() {
  const { organization, user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = organization?.id;

  // Fetch custom roles
  const {
    data: customRoles,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['custom-roles', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('custom_roles')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name');

      if (error) throw error;
      return data as CustomRole[];
    },
    enabled: !!organizationId,
  });

  // Fetch permissions for a specific custom role
  const fetchRolePermissions = async (roleId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from('custom_role_permissions')
      .select('permission_id')
      .eq('custom_role_id', roleId);

    if (error) throw error;
    return (data || []).map(p => p.permission_id);
  };

  // Create custom role
  const createCustomRole = useMutation({
    mutationFn: async (input: CreateCustomRoleInput) => {
      if (!organizationId) throw new Error('No organization');

      // Create the role
      const { data: role, error: roleError } = await supabase
        .from('custom_roles')
        .insert({
          organization_id: organizationId,
          name: input.name,
          description: input.description || null,
          base_role: input.base_role || null,
          color: input.color || '#6366f1',
          created_by: user?.id,
        })
        .select()
        .single();

      if (roleError) throw roleError;

      // Add permissions if provided
      if (input.permissions && input.permissions.length > 0) {
        const { error: permError } = await supabase
          .from('custom_role_permissions')
          .insert(
            input.permissions.map(permId => ({
              custom_role_id: role.id,
              permission_id: permId,
            }))
          );

        if (permError) throw permError;
      }

      return role as CustomRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Update custom role
  const updateCustomRole = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCustomRoleInput }) => {
      const { data, error } = await supabase
        .from('custom_roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CustomRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Delete custom role
  const deleteCustomRole = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('custom_roles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Update role permissions
  const updateRolePermissions = useMutation({
    mutationFn: async ({ roleId, permissionIds }: { roleId: string; permissionIds: string[] }) => {
      // Delete existing permissions
      await supabase
        .from('custom_role_permissions')
        .delete()
        .eq('custom_role_id', roleId);

      // Insert new permissions
      if (permissionIds.length > 0) {
        const { error } = await supabase
          .from('custom_role_permissions')
          .insert(
            permissionIds.map(permId => ({
              custom_role_id: roleId,
              permission_id: permId,
            }))
          );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Add single permission to role
  const addPermissionToRole = useMutation({
    mutationFn: async ({ roleId, permissionId }: { roleId: string; permissionId: string }) => {
      const { error } = await supabase
        .from('custom_role_permissions')
        .insert({
          custom_role_id: roleId,
          permission_id: permissionId,
        });

      if (error && error.code !== '23505') throw error; // Ignore duplicate key
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Remove single permission from role
  const removePermissionFromRole = useMutation({
    mutationFn: async ({ roleId, permissionId }: { roleId: string; permissionId: string }) => {
      const { error } = await supabase
        .from('custom_role_permissions')
        .delete()
        .eq('custom_role_id', roleId)
        .eq('permission_id', permissionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles', organizationId] });
    },
  });

  // Assign custom role to user
  const assignCustomRoleToUser = useMutation({
    mutationFn: async ({ userId, customRoleId }: { userId: string; customRoleId: string | null }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ custom_role_id: customRoleId })
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });

  return {
    // Data
    customRoles: customRoles || [],
    isLoading,
    error,

    // Mutations
    createCustomRole,
    updateCustomRole,
    deleteCustomRole,
    updateRolePermissions,
    addPermissionToRole,
    removePermissionFromRole,
    assignCustomRoleToUser,

    // Helpers
    fetchRolePermissions,
    refetch,
  };
}

















